import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { RoleDefinition, RoleCriteria } from './role-definitions.service';
import { QuickBuild, UIFilter, UniqueIdGenerator } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { ReportDemographicsParams } from 'ldt-people-api';
import * as dayjs from 'dayjs';

// TODO: we CAN move this to the role-definition.service, but it's nice to have it separately since it depends on the PeopleReportService, not the SavedItemsService
@Injectable({ providedIn: 'root' })
export class RoleTitlesService {
  constructor(private peopleReportService: PeopleReportService) {}

  public getTopMatchingTitlesForRole(
    role: RoleDefinition,
    limit: number | null = null
  ): Observable<string[]> {
    return this.getTopMatchingTitlesFromCriteria(role.criteria, role.name, limit);
  }

  public getTopMatchingTitlesFromCriteria(
    criteria: RoleCriteria,
    roleName: string | undefined,
    limit: number | null = null
  ): Observable<string[]> {
    return this.getMatchingTitlesWithStats(criteria, roleName, limit).pipe(
      map((result) => result.titles.map(t => t.title))
    );
  }

  public getMatchingTitlesWithStats(
    criteria: RoleCriteria,
    roleName: string | undefined,
    limit: number | null = null
  ): Observable<{ titles: { title: string; peopleCount: number }[]; totalPeople: number; totalTitles: number }> {
    if (!criteria) {
      return of({ titles: [], totalPeople: 0, totalTitles: 0 });
    }

    const mustExact = criteria.mustExact ?? [];
    const mustFuzzy = criteria.mustFuzzy ?? [];
    const mustNotExact = criteria.mustNotExact ?? [];
    const mustNotFuzzy = criteria.mustNotFuzzy ?? [];

    const filters: UIFilter[] = [];

    if (mustExact.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'exact',
          string_values: mustExact,
          type: 'must',
        },
      });
    }

    if (mustFuzzy.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'fuzzy',
          string_values: mustFuzzy,
          type: 'must',
        },
      });
    }

    if (mustNotExact.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'exact',
          string_values: mustNotExact,
          type: 'must_not',
        },
      });
    }

    if (mustNotFuzzy.length > 0) {
      filters.push({
        filter: {
          field: 'jobs.title',
          match_type: 'fuzzy',
          string_values: mustNotFuzzy,
          type: 'must_not',
        },
      });
    }

    const qb: QuickBuild = {
      id: UniqueIdGenerator.generate(),
      name: (roleName && roleName.trim()) || 'RoleBuilder',
      rootFilterGroup: {
        id: UniqueIdGenerator.generate(),
        operator: 'and',
        filters: [
          {
            id: 'rolebuilder',
            operator: 'and',
            isJobsGroup: true,
            filters,
          },
        ],
      },
    };

    const searchParams: ReportDemographicsParams = {
      group_by: ['jobs.title'],
      aggregate_type: 'range',
      status: 'active',
      date_from: dayjs().subtract(2, 'year').format('YYYY-MM-DD'),
      date_to: dayjs().format('YYYY-MM-DD'),
    };

    return this.peopleReportService
      .getReportDataWithReportDetails(
        'rolebuilder-job-titles',
        'demographics',
        searchParams,
        'rolebuilder',
        qb
      )
      .pipe(
        map((data) => {
          const demographicsData = data.data.demographics || [];
          let titlesWithCounts = demographicsData.map((d: any) => ({
            title: d.group_values[0].value,
            peopleCount: d.count_employees || 0
          }));
          if (limit) {
            titlesWithCounts = titlesWithCounts.slice(0, limit);
          }
          return {
            titles: titlesWithCounts,
            totalPeople: data.count || 0,
            totalTitles: (data as any).report_cohort_count || 0,
          };
        })
      );
  }
}
